import { logger, generateRequestId } from '../utils/logger';

/**
 * Offline Storage Service
 * Manages offline check-in data using IndexedDB
 * Provides automatic sync when connectivity restores
 */

const DB_NAME = 'MyBodyTodayOfflineDB';
const DB_VERSION = 1;
const STORE_NAME = 'pendingCheckIns';

class OfflineStorageService {
  constructor() {
    this.db = null;
    this.initPromise = this.initDB();
  }

  /**
   * Initialize IndexedDB
   */
  async initDB() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        logger?.error('Failed to open IndexedDB', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        logger?.debug('IndexedDB initialized successfully');
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        
        // Create object store for pending check-ins
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const objectStore = db.createObjectStore(STORE_NAME, { 
            keyPath: 'id', 
            autoIncrement: true 
          });
          
          // Create indexes for querying
          objectStore.createIndex('timestamp', 'timestamp', { unique: false });
          objectStore.createIndex('userId', 'userId', { unique: false });
          objectStore.createIndex('syncStatus', 'syncStatus', { unique: false });
          
          logger?.debug('IndexedDB object store created');
        }
      };
    });
  }

  /**
   * Ensure DB is initialized before operations
   */
  async ensureDB() {
    if (!this.db) {
      await this.initPromise;
    }
    return this.db;
  }

  /**
   * Save check-in data offline
   * @param {object} checkInData - Check-in entry data
   * @param {string} userId - User ID
   * @returns {Promise<{success: boolean, id?: number, error?: string}>}
   */
  async saveOfflineCheckIn(checkInData, userId) {
    const requestId = generateRequestId();
    
    try {
      await this.ensureDB();
      
      const entry = {
        ...checkInData,
        userId,
        timestamp: new Date()?.toISOString(),
        syncStatus: 'pending',
        createdOffline: true,
        retryCount: 0
      };

      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction([STORE_NAME], 'readwrite');
        const objectStore = transaction.objectStore(STORE_NAME);
        const request = objectStore.add(entry);

        request.onsuccess = () => {
          logger?.debug('Check-in saved offline', { requestId, id: request.result });
          resolve({ success: true, id: request.result });
        };

        request.onerror = () => {
          logger?.error('Failed to save offline check-in', request.error, { requestId });
          reject({ success: false, error: request.error.message });
        };
      });
    } catch (error) {
      logger?.error('Offline storage error', error, { requestId });
      return { success: false, error: error?.message };
    }
  }

  /**
   * Get all pending check-ins for sync
   * @param {string} userId - User ID
   * @returns {Promise<Array>}
   */
  async getPendingCheckIns(userId) {
    try {
      await this.ensureDB();

      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction([STORE_NAME], 'readonly');
        const objectStore = transaction.objectStore(STORE_NAME);
        const index = objectStore.index('userId');
        const request = index.getAll(userId);

        request.onsuccess = () => {
          const pending = request.result.filter(entry => entry.syncStatus === 'pending');
          logger?.debug('Retrieved pending check-ins', { count: pending.length });
          resolve(pending);
        };

        request.onerror = () => {
          logger?.error('Failed to retrieve pending check-ins', request.error);
          reject(request.error);
        };
      });
    } catch (error) {
      logger?.error('Error retrieving pending check-ins', error);
      return [];
    }
  }

  /**
   * Get count of pending check-ins
   * @param {string} userId - User ID
   * @returns {Promise<number>}
   */
  async getPendingCount(userId) {
    try {
      const pending = await this.getPendingCheckIns(userId);
      return pending?.length;
    } catch (error) {
      logger?.error('Error getting pending count', error);
      return 0;
    }
  }

  /**
   * Mark check-in as synced
   * @param {number} id - Entry ID
   * @returns {Promise<boolean>}
   */
  async markAsSynced(id) {
    try {
      await this.ensureDB();

      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction([STORE_NAME], 'readwrite');
        const objectStore = transaction.objectStore(STORE_NAME);
        const getRequest = objectStore.get(id);

        getRequest.onsuccess = () => {
          const entry = getRequest.result;
          if (entry) {
            entry.syncStatus = 'synced';
            entry.syncedAt = new Date().toISOString();
            
            const updateRequest = objectStore.put(entry);
            
            updateRequest.onsuccess = () => {
              logger?.debug('Check-in marked as synced', { id });
              resolve(true);
            };
            
            updateRequest.onerror = () => {
              logger?.error('Failed to mark as synced', updateRequest.error);
              reject(false);
            };
          } else {
            resolve(false);
          }
        };

        getRequest.onerror = () => {
          logger?.error('Failed to retrieve entry for sync update', getRequest.error);
          reject(false);
        };
      });
    } catch (error) {
      logger?.error('Error marking as synced', error);
      return false;
    }
  }

  /**
   * Mark check-in as failed
   * @param {number} id - Entry ID
   * @param {string} errorMessage - Error message
   * @returns {Promise<boolean>}
   */
  async markAsFailed(id, errorMessage) {
    try {
      await this.ensureDB();

      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction([STORE_NAME], 'readwrite');
        const objectStore = transaction.objectStore(STORE_NAME);
        const getRequest = objectStore.get(id);

        getRequest.onsuccess = () => {
          const entry = getRequest.result;
          if (entry) {
            entry.syncStatus = 'failed';
            entry.lastError = errorMessage;
            entry.retryCount = (entry.retryCount || 0) + 1;
            entry.lastAttempt = new Date().toISOString();
            
            const updateRequest = objectStore.put(entry);
            
            updateRequest.onsuccess = () => {
              logger?.debug('Check-in marked as failed', { id, retryCount: entry.retryCount });
              resolve(true);
            };
            
            updateRequest.onerror = () => {
              logger?.error('Failed to mark as failed', updateRequest.error);
              reject(false);
            };
          } else {
            resolve(false);
          }
        };

        getRequest.onerror = () => {
          logger?.error('Failed to retrieve entry for failure update', getRequest.error);
          reject(false);
        };
      });
    } catch (error) {
      logger?.error('Error marking as failed', error);
      return false;
    }
  }

  /**
   * Delete synced check-ins older than specified days
   * @param {number} daysOld - Number of days
   * @returns {Promise<number>} - Number of deleted entries
   */
  async cleanupSyncedEntries(daysOld = 7) {
    try {
      await this.ensureDB();
      const cutoffDate = new Date();
      cutoffDate?.setDate(cutoffDate?.getDate() - daysOld);

      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction([STORE_NAME], 'readwrite');
        const objectStore = transaction.objectStore(STORE_NAME);
        const request = objectStore.openCursor();
        let deletedCount = 0;

        request.onsuccess = (event) => {
          const cursor = event.target.result;
          if (cursor) {
            const entry = cursor.value;
            if (entry.syncStatus === 'synced' && new Date(entry.syncedAt) < cutoffDate) {
              cursor.delete();
              deletedCount++;
            }
            cursor.continue();
          } else {
            logger?.debug('Cleaned up synced entries', { deletedCount });
            resolve(deletedCount);
          }
        };

        request.onerror = () => {
          logger?.error('Failed to cleanup synced entries', request.error);
          reject(0);
        };
      });
    } catch (error) {
      logger?.error('Error during cleanup', error);
      return 0;
    }
  }

  /**
   * Get all entries (for debugging)
   * @returns {Promise<Array>}
   */
  async getAllEntries() {
    try {
      await this.ensureDB();

      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction([STORE_NAME], 'readonly');
        const objectStore = transaction.objectStore(STORE_NAME);
        const request = objectStore.getAll();

        request.onsuccess = () => {
          resolve(request.result);
        };

        request.onerror = () => {
          logger?.error('Failed to retrieve all entries', request.error);
          reject([]);
        };
      });
    } catch (error) {
      logger?.error('Error retrieving all entries', error);
      return [];
    }
  }

  /**
   * Clear all data (for testing/reset)
   * @returns {Promise<boolean>}
   */
  async clearAll() {
    try {
      await this.ensureDB();

      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction([STORE_NAME], 'readwrite');
        const objectStore = transaction.objectStore(STORE_NAME);
        const request = objectStore.clear();

        request.onsuccess = () => {
          logger?.debug('All offline data cleared');
          resolve(true);
        };

        request.onerror = () => {
          logger?.error('Failed to clear offline data', request.error);
          reject(false);
        };
      });
    } catch (error) {
      logger?.error('Error clearing offline data', error);
      return false;
    }
  }
}

// Export singleton instance
const offlineStorageService = new OfflineStorageService();
export default offlineStorageService;